import { useMemo, useState } from "react";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import Table from "@components/layout/Table";
import Loading from "@components/shared/Loading";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import Tag from "@components/shared/Tag";
import ConfirmModal from "@components/shared/ConfirmModal";
import NoteIndicatorBadge from "@components/notes/NoteIndicatorBadge";
import RowNotesModal from "@components/notes/RowNotesModal";
import ENV from "@config/env";
import { useAppContext } from "@context/AppContext";
import { useAllLevel1Resources } from "@hooks/query/useAllLevel1Resources";
import { useNoteCounts } from "@hooks/query/useNoteCounts";
import { useCancelSubscriberSyncDraft } from "@hooks/mutation/useCancelSubscriberSyncDraft";
import { toastError, toastSuccess } from "@utils/toast";
import { DBTables } from "@utils/constants/dbTables";
import { formatDateTime } from "@utils/formatDateTime";
import {
  getAllSubscriberSyncUploads,
  getSubscriberSyncUploadsByLevel1WRS,
  downloadResult,
  UploadHistoryItem,
} from "@services/subscriberSyncUpload";

type Props = {
  level1Resource?: Record<string, any>;
};

// Numeric status codes mirror the API-side SubscriberSyncUploadStatus / SubscriberSyncUploadRowStatus
// enums (api/app/Models/SubscriberSyncUpload).
const ApiSubscriberSyncUploadStatus = {
  Draft: 0,
  PartiallyCompleted: 1,
  Completed: 2,
} as const;

const ApiSubscriberSyncUploadRowStatus = {
  Draft: 0,
  Success: 1,
  Failed: 2,
} as const;

const STATUS_LABEL_KEYS: Record<number, string> = {
  [ApiSubscriberSyncUploadStatus.Draft]: "subscriber_sync_upload.status.draft",
  [ApiSubscriberSyncUploadStatus.PartiallyCompleted]:
    "subscriber_sync_upload.status.partially_completed",
  [ApiSubscriberSyncUploadStatus.Completed]: "subscriber_sync_upload.status.completed",
};

const STATUS_TAG: Record<number, "info" | "warning" | "success"> = {
  [ApiSubscriberSyncUploadStatus.Draft]: "info",
  [ApiSubscriberSyncUploadStatus.PartiallyCompleted]: "warning",
  [ApiSubscriberSyncUploadStatus.Completed]: "success",
};

const SubscriberSyncUploadTable: React.FunctionComponent<Props> = ({ level1Resource }) => {
  const { checkPermissions } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Two query modes — scheme-scoped vs cross-scheme — kept as separate keys so the cached
  // global list isn't reused as a scheme-filtered list (and vice versa).
  const isSchemeScoped = Boolean(level1Resource?.id);
  const { data: uploads, isLoading } = useQuery(
    isSchemeScoped
      ? ["subscriberSyncUploads", level1Resource!.id]
      : ["subscriberSyncUploads", "all"],
    () =>
      isSchemeScoped
        ? getSubscriberSyncUploadsByLevel1WRS(level1Resource!.id)
        : getAllSubscriberSyncUploads(),
  );

  const { data: allSchemes = [] } = useAllLevel1Resources({ enabled: !isSchemeScoped });
  const schemeNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of allSchemes as any[]) map[s.id] = s.name;
    return map;
  }, [allSchemes]);

  const deleteDraft = useCancelSubscriberSyncDraft({
    onSuccess: () => {
      toastSuccess(t("bulk_meter_upload.message.draft_deleted"));
    },
    onError: (error: any) => {
      toastError(
        error?.response?.data?.message ?? t("bulk_meter_upload.message.draft_delete_error"),
      );
    },
  });

  const [filter, setFilter] = useState<{
    fileName: string;
    level1ResourceId?: string;
  }>({ fileName: "" });
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [openNotesRow, setOpenNotesRow] = useState<{ id: string; label: string } | null>(null);

  const uploadIds = useMemo(() => (uploads ?? []).map(u => u.id), [uploads]);
  const { data: noteCounts = {} } = useNoteCounts(DBTables.SubscriberSyncUploads, uploadIds);
  const canViewNotes = checkPermissions(["ViewNotes"]);
  const canUpload = checkPermissions(["BulkUploadSubscriberSync"]);

  const handleConfirmDeleteDraft = async () => {
    if (!deletingDraftId) return;
    try {
      await deleteDraft.mutateAsync(deletingDraftId);
    } finally {
      setDeletingDraftId(null);
    }
  };

  const handleDownloadResult = async (id: string) => {
    try {
      const blob = await downloadResult(id);
      const url = URL.createObjectURL(blob);
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: `subscriber-sync-result-${id}.xlsx`,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: error?.response?.data?.message ?? error?.message ?? "unknown",
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const tableFields = [
    ...(isSchemeScoped
      ? []
      : [{ title: t("common.level1wrs"), name: "level1Resource" }]),
    { title: t("common.file_uploaded.file"), name: "title" },
    { title: t("bulk_meter_upload.history.uploaded_by"), name: "uploadedBy" },
    { title: t("bulk_meter_upload.history.uploaded_at"), name: "uploadedAt" },
    { title: t("bulk_meter_upload.history.status"), name: "status" },
    { title: t("bulk_meter_upload.history.rows"), name: "rows" },
    ...(canViewNotes ? [{ title: t("subscriber.notes"), name: "notes" }] : []),
    { title: t("common.action"), name: "action" },
  ];

  const rowCounts = (rows: UploadHistoryItem["rows"] = []) => ({
    total: rows.length,
    success: rows.filter(r => r.status === ApiSubscriberSyncUploadRowStatus.Success).length,
  });

  const tableData = (uploads ?? [])
    .filter(
      item =>
        !filter.fileName?.length ||
        (item.fileName ?? "").toLowerCase().includes(filter.fileName.toLowerCase()),
    )
    .filter(
      item =>
        isSchemeScoped ||
        !filter.level1ResourceId ||
        item.level1ResourceId === filter.level1ResourceId,
    )
    .map(item => {
      const status: number = item.status ?? ApiSubscriberSyncUploadStatus.Completed;
      const counts = rowCounts(item.rows);
      const fileName = item.fileName ?? "—";
      const canResume =
        status === ApiSubscriberSyncUploadStatus.Draft ||
        status === ApiSubscriberSyncUploadStatus.PartiallyCompleted;
      const hasResult =
        status === ApiSubscriberSyncUploadStatus.PartiallyCompleted ||
        status === ApiSubscriberSyncUploadStatus.Completed;

      const resumeLevel1ResourceId = level1Resource?.id ?? item.level1ResourceId;
      const resumeLevel1Resource =
        level1Resource ?? {
          id: item.level1ResourceId,
          name: schemeNameById[item.level1ResourceId] ?? "",
        };

      return {
        level1Resource: schemeNameById[item.level1ResourceId] ?? "—",
        title: fileName,
        uploadedBy: item.uploadedByUser?.name ?? "—",
        uploadedAt: formatDateTime(new Date(item.updatedAt ?? item.createdAt)),
        status: (
          <Tag status={STATUS_TAG[status] ?? "warning"}>
            {t(STATUS_LABEL_KEYS[status] ?? STATUS_LABEL_KEYS[ApiSubscriberSyncUploadStatus.Draft])}
          </Tag>
        ),
        rows:
          counts.total === 0
            ? "—"
            : status === ApiSubscriberSyncUploadStatus.Draft
              ? `${counts.total}`
              : `${counts.success}/${counts.total}`,
        notes: (
          <NoteIndicatorBadge
            count={noteCounts[item.id]}
            onClick={() => setOpenNotesRow({ id: item.id, label: fileName })}
          />
        ),
        action: (() => {
          const options = [
            ...(canResume && canUpload
              ? [
                  {
                    label: t("bulk_meter_upload.resume"),
                    value: "resume",
                    onSelect: () =>
                      navigate(
                        `/polestar/level1wrs/${resumeLevel1ResourceId}/subscriber_sync_upload?draftId=${item.id}`,
                        { state: { level1Resource: resumeLevel1Resource } },
                      ),
                  },
                ]
              : []),
            ...(hasResult
              ? [
                  {
                    label: t("subscriber_sync_upload.summary.download_result"),
                    value: "download",
                    onSelect: () => handleDownloadResult(item.id),
                  },
                ]
              : []),
            ...(status === ApiSubscriberSyncUploadStatus.Draft && canUpload
              ? [
                  {
                    label: t("common.delete"),
                    value: "delete",
                    onSelect: () => setDeletingDraftId(item.id),
                  },
                ]
              : []),
          ];

          return options.length > 0 ? (
            <Select
              placeholder={t("common.actions")}
              options={options}
              onChange={e => (e as any)?.onSelect?.()}
              menuPortalTarget={document.body}
              isSearchable={false}
              value={null}
            />
          ) : null;
        })(),
      };
    });

  const handleFilterChange = (field: string, value: any) => {
    setFilter({ ...filter, [field]: value });
  };

  return (
    <div className="flex flex-col grow gap-4">
      <div className="flex justify-between items-end">
        <form className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3 2xl:w-2/3 relative z-20">
          {!isSchemeScoped && (
            <div>
              <Label htmlFor="level1Resource">{t("common.level1wrs")}</Label>
              <SelectLevel1Resource
                inputId="level1Resource"
                onChange={e =>
                  handleFilterChange("level1ResourceId", e?.value ?? undefined)
                }
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          )}
          <div>
            <Label htmlFor="fileName">{t("bulk_meter_upload.filter.file_name")}</Label>
            <SearchInput
              id="fileName"
              onChange={e => handleFilterChange("fileName", e.target.value)}
            />
          </div>
        </form>

        {canUpload && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-sm rounded"
              // Static template asset. Tenant-namespaced for seqwater (matches the wizard's own
              // download action) so adding the button here surfaces it at both the global and
              // scheme-level table without duplicating logic per page.
              onClick={() => {
                const url = `${process.env.PUBLIC_URL}${
                  ENV.CLIENT_ID === "seqwater" ? "/seqwater" : ""
                }/bulk_customer_template.xlsx`;
                const link = Object.assign(document.createElement("a"), {
                  href: url,
                  download: "bulk_customer_template.xlsx",
                });
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
            >
              {t("subscriber_sync_upload.download_template")}
            </button>
            <Link
              className="btn-secondary text-sm rounded"
              to={
                isSchemeScoped
                  ? `/polestar/level1wrs/${level1Resource?.id}/subscriber_sync_upload`
                  : `/polestar/subscribers/sync_uploads/new`
              }
              state={isSchemeScoped ? { level1Resource } : undefined}
            >
              {t("subscriber_sync_upload.action.upload")}
            </Link>
          </div>
        )}
      </div>
      <Table
        containerClassName="rounded-none md:rounded-none text-sm"
        fields={tableFields}
        data={tableData}
        stickyHeader
      />

      <ConfirmModal
        open={!!deletingDraftId}
        onClose={() => setDeletingDraftId(null)}
        onConfirm={handleConfirmDeleteDraft}
        isSubmitting={deleteDraft.isLoading}
        confirmText={t("common.delete")}
      >
        {t("bulk_meter_upload.delete_draft_confirm")}
      </ConfirmModal>

      <RowNotesModal
        open={openNotesRow !== null}
        referenceTable={DBTables.SubscriberSyncUploads}
        referenceId={openNotesRow?.id ?? ""}
        artifactName={openNotesRow?.label}
        onClose={() => setOpenNotesRow(null)}
      />
    </div>
  );
};

export default SubscriberSyncUploadTable;
